import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, TriangleAlert, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

type Props<T, M extends boolean = false, N extends boolean = false> = {
  id?: string;
  label?: string;
  options: T[];
  value: M extends true ? T[] : T | null;
  onChange: (value: N extends false ? (M extends true ? T[] : T) | null : M extends true ? T[] : T) => void;
  getOptionLabel: (value: T | null) => string | undefined;
  getOptionValue: (value: T | null) => string | undefined;
  renderOption?: (option: T) => React.ReactNode;
  renderNoOption?: React.ReactNode;
  groupBy?: keyof T;
  loader?: boolean;
  multiple?: M;
  className?: string;
  required?: boolean;
  popoverClassName?: string;
  errorMessage?: string;
  disableClearable?: N;
  placeholder?: string;
  disabled?: boolean;
  onInputChange?: (value: string) => void;
  visibleLimit?: number;
};

const Autocomplete = <T, M extends boolean = false, N extends boolean = false>({
  id,
  label,
  value,
  options,
  onChange,
  renderOption,
  getOptionLabel,
  getOptionValue,
  renderNoOption,
  errorMessage,
  groupBy,
  loader,
  multiple,
  className,
  required,
  popoverClassName,
  disableClearable,
  placeholder,
  disabled,
  onInputChange,
  visibleLimit = 50,
}: Props<T, M, N>) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const groupedOptions = useMemo(
    () =>
      groupBy
        ? options.reduce<Record<string, T[]>>((acc, item) => {
            const groupName = (item[groupBy] ?? "") as string;
            if (!acc[groupName]) acc[groupName] = [];
            acc[groupName].push(item);
            return acc;
          }, {})
        : { "": options },
    [options, groupBy],
  );

  const renderValue = useCallback(
    (value: T | T[] | null) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return placeholder || "Select";
      if (Array.isArray(value)) {
        return (
          <div className="flex flex-row flex-wrap gap-1">
            {value.map((v, i) => (
              <Badge variant={"outline"} key={v + "-" + i} className="bg-muted flex flex-row items-center gap-1">
                {getOptionLabel(v)}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const list = Array.isArray(value) ? value : [];
                    onChange(list.filter((val) => getOptionValue(val) !== getOptionValue(v)) as M extends true ? T[] : T);
                  }}
                  onMouseEnter={(e) => e.stopPropagation()}
                  className="hover:text-destructive text-muted-foreground hover:bg-destructive/10 rounded-lg"
                >
                  <X className="h-4 w-4" />
                </button>
              </Badge>
            ))}
          </div>
        );
      }
      return getOptionLabel(value);
    },
    [getOptionLabel, onChange, getOptionValue, placeholder],
  );

  const filteredOptions = useCallback(
    (options: T[]) => {
      if (inputValue.length === 0) {
        return options.slice(0, visibleLimit);
      }
      return options.filter((option) => (getOptionLabel(option)?.toLowerCase() ?? "").toLowerCase().includes(inputValue.toLowerCase())).slice(0, visibleLimit);
    },
    [getOptionLabel, inputValue, visibleLimit],
  );

  return (
    <div>
      {label && <Label className={(required && !value) || (errorMessage ?? "").length > 0 ? "text-destructive" : ""}>{label}</Label>}
      <Popover open={open} onOpenChange={(o) => setOpen(o)}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            className={cn(
              "justify-start min-w-[200px]",
              className,
              !value || (Array.isArray(value) && value.length === 0) ? "text-muted-foreground" : "",
              required && !value && "text-destructive border-destructive",
              multiple && "h-auto",
            )}
            disabled={loader || disabled}
          >
            <div className=" text-start flex-1 truncate">{renderValue(value)}</div>
            {!(!value || (Array.isArray(value) && value.length === 0)) && !disableClearable && (
              <span
                className="text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null as any);
                }}
              >
                <X />
              </span>
            )}
            <ChevronDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        {errorMessage && (
          <p className={cn(" bg-destructive mt-2 text-md flex gap-2 p-3 font-medium text-destructive-foreground", className)}>
            <span className="w-8">
              <TriangleAlert />
            </span>
            {errorMessage}
          </p>
        )}
        <PopoverContent className={cn("p-0", popoverClassName)}>
          <Command
            // filter={(value, search, keywords) => {
            //   if (search.length === 0) {
            //     return 1;
            //   }
            //   return keywords?.some((keyword) => keyword.toLowerCase().includes(search.toLowerCase())) ? 1 : 0;
            // }}
            shouldFilter={false}
          >
            <CommandInput
              className="h-9"
              value={inputValue}
              onValueChange={(e) => {
                setInputValue(e);
                onInputChange?.(e);
              }}
            />
            <CommandList>
              <CommandEmpty>{renderNoOption ?? "Not found"}</CommandEmpty>

              {Object.entries(groupedOptions).map(
                ([group, options]) =>
                  filteredOptions(options).length > 0 && (
                    <CommandGroup key={group} heading={group}>
                      {filteredOptions(options).map((item, index) => {
                        const isSelected = Array.isArray(value) ? value.some((v) => getOptionValue(v) === getOptionValue(item)) : getOptionValue(value as T) === getOptionValue(item);
                        return (
                          <CommandItem
                            key={getOptionValue(item)}
                            value={getOptionValue(item)?.toString()}
                            // keywords={[getOptionLabel(item)?.toLowerCase() ?? ""]}
                            onSelect={() => {
                              if (multiple) {
                                const list = Array.isArray(value) ? value : [];
                                const exists = list.find((v) => getOptionValue(v) === getOptionValue(item));
                                const newValue = exists ? list.filter((v) => getOptionValue(v) !== getOptionValue(item)) : [...list, item];
                                onChange(newValue as M extends true ? T[] : T);
                              } else {
                                onChange(item as M extends true ? T[] : T);
                                setOpen(false);
                              }
                            }}
                          >
                            {renderOption?.(item) ?? getOptionLabel(item)}
                            <Check className={cn("ml-auto", isSelected ? "opacity-100" : "opacity-0")} />
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ),
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default Autocomplete;
